import {
  Grid,
  Box,
  Paper,
  Input,
  Button,
  Alert,
  Theme,
} from '@prometheus/components';
import { userSignIn } from '@prometheus/redux';
import React from 'react';
import { useRequest } from '@prometheus/hooks';
import { useDispatch, useSelector } from 'react-redux';
import { LogoLarge } from '../Logo/LogoLarge';
import ReactMarkdown from 'react-markdown';
import {
  FormControl,
  LoginFormContainer,
  LoginFormHeader,
  LogoContainer,
  WelcomeText,
  Divider,
  CorporatePolicyText,
  CorporatePolicyLink,
} from './LoginPage.styles';
import { AppState } from '@prometheus/types';
import { useLoginOptions } from './LoginPage.hook';
import { getSettings } from 'src/api/configure';
import { useTranslation } from 'react-i18next';

export const LoginPage: React.FC<{ hasError: boolean }> = ({ hasError }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { loginOptions, status } = useLoginOptions();
  const { state: appSettings } = useRequest(getSettings);
  const [userName, setUserName] = React.useState<string>('');
  const [password, setPassword] = React.useState<string>('');
  const errorMessage = useSelector<AppState, string>(
    (state: any) => state.user.errorMessage
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    dispatch(userSignIn(userName, password));
  };

  function LinkRenderer(props: any) {
    return (
      <CorporatePolicyLink href={props.href} target="_blank" rel="noreferrer">
        {props.children}
      </CorporatePolicyLink>
    );
  }

  return (
    <Grid container height="100vh">
      <LogoContainer
        item
        container
        xs={0}
        md={6}
        sx={(theme: Theme) => ({
          display: { xs: 'none', md: 'flex' },
          boxShadow: theme.customShadows[2],
        })}
      >
        <Grid item xs={5} mx="auto" my="auto">
          <LogoLarge color="white" />
        </Grid>
      </LogoContainer>
      <LoginFormContainer item container xs={12} md={6}>
        <Box minWidth={450} width="50%" mx="auto">
          <form onSubmit={handleSubmit}>
            <Paper>
              {hasError && <Alert severity="error">{errorMessage}</Alert>}

              <Grid container p={4} spacing={3}>
                <Grid item xs={12} spacing={2} container>
                  <WelcomeText item xs={12}>
                    {t('modules.layout.login.welcome_text', {
                      app: 'Prometheus',
                    })}
                  </WelcomeText>
                  <LoginFormHeader item xs={12}>
                    {t(
                      'modules.layout.login.login_header',
                      'Login into your account'
                    )}
                  </LoginFormHeader>
                </Grid>

                <Grid item xs={12} spacing={2} container>
                  <FormControl item xs={12}>
                    <label>{t('forms.username', 'Username')}</label>
                    <Input
                      fullWidth
                      value={userName}
                      onChange={e => setUserName(e.target.value)}
                      placeholder={t(
                        'forms.username_placeholder',
                        'Enter Username...'
                      )}
                    />
                  </FormControl>
                  <FormControl item xs={12}>
                    <label>{t('forms.password', 'Password')}</label>
                    <Input
                      fullWidth
                      type="password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder={t(
                        'forms.password_placeholder',
                        'Enter Password...'
                      )}
                    />
                  </FormControl>
                  <Grid item xs={12} textAlign="right">
                    <Button
                      variant="contained"
                      color="success"
                      type="submit"
                      fullWidth
                      disabled={!userName || !password}
                    >
                      {t('buttons.login', 'Log In')}
                    </Button>
                  </Grid>

                  {Object.keys(loginOptions).map((key, index) => (
                    <Grid item xs={12} key={key + index}>
                      {index === 0 && <Divider />}
                      <Button
                        onClick={() => {
                          location.href = `/oauth2/authorization/${key}`;
                        }}
                        variant="outlined"
                        fullWidth
                        startIcon={
                          loginOptions[key].image ? (
                            <img src={loginOptions[key].image} height={16} />
                          ) : undefined
                        }
                      >
                        {loginOptions[key].text}
                      </Button>
                    </Grid>
                  ))}
                  {appSettings?.features?.corporatePolicy && (
                    <Grid item>
                      <div>
                        <ReactMarkdown
                          components={{
                            a: LinkRenderer,
                            p: CorporatePolicyText,
                          }}
                        >
                          {appSettings.features.corporatePolicy.text}
                        </ReactMarkdown>
                      </div>
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </Paper>
          </form>
        </Box>
      </LoginFormContainer>
    </Grid>
  );
};
